<template>
  <ValidationObserver
    ref="dialogObserver"
    slim
  >
    <AppDialog
      :title="isEditMode ? 'Edit global to-do' : 'Add a global to-do'"
      append-to-body
      show
      data-testid="global-todo-form-modal"
      @close="closeDialog"
    >
      <form
        id="global-todo-form"
        autocomplete="off"
        data-testid="global-todo-form"
        @submit.prevent="onFormSubmit"
      >
        <ValidationProvider
          v-slot="{ errors }"
          name="name"
          rules="required"
          :model-value="form.name"
        >
          <div
            class="form-group"
            :class="{ 'has-error': errors[0] || form.errors.name }"
            data-testid="global-todo-name-input"
          >
            <label
              class="control-label"
              for="global-todo-name"
            >
              Name
              <abbr title="required">*</abbr>
            </label>
            <AppInput
              id="global-todo-name"
              v-model="form.name"
              type="text"
              class="form-control"
              placeholder="Name this to-do"
              required
            />
            <AppDialogError
              :errors="errors"
              :form-errors="form.errors.name"
            />
          </div>
        </ValidationProvider>

        <div
          class="form-group"
          :class="{ 'has-error': form.errors.project_ids }"
        >
          <a
            href="#"
            class="pull-right select-all"
            data-testid="global-todo-select-all-projects"
            @click.stop.prevent="$refs.projectSelector.onSelectAll()"
          >Select all</a>
          <AppLazySelect
            ref="projectSelector"
            v-model="selectedProjectIds"
            :fetcher="fetchProjects"
            eager
            multiple
            resource="project"
            label="Projects selected"
          >
          </AppLazySelect>
          <AppDialogError :errors="form.errors.project_ids" />
        </div>

        <div class="dialog-warning blue mb-0">
          <i class="hi hi-info-filled hi-12 pt-0 d-flex align-items-center"></i>
          <div v-if="!isEditMode">
            All members added to selected projects will be able to track time to global to-dos
          </div>
          <div v-else>
            Changes will be reflected in any project where this global to-do is applied and will only be active after
            they're saved.
          </div>
        </div>

        <div
          class="form-group"
          data-testid="global-todo-add-all-future-projects"
        >
          <AppCheckbox
            v-model="form.add_new_projects_enabled"
            class="v2"
          >
            Add all future projects
            <i
              v-tooltip="{ placement: 'bottom' }"
              title="Automatically add this global to-do to all future projects"
              class="a-tooltip hi hi-info-filled text-lighter ml-5"
            />
          </AppCheckbox>
        </div>
      </form>
      <template #footer>
        <div class="d-flex justify-content-end">
          <AppButton
            type="default"
            @click="closeDialog"
          >
            Cancel
          </AppButton>
          <button
            class="btn btn-primary"
            type="submit"
            form="global-todo-form"
            data-testid="global-todo-form-save"
            :disabled="!form.name || !form.name.trim() || form.processing"
          >
            {{ form.processing ? 'Saving...' :'Save' }}
          </button>
        </div>
      </template>
    </AppDialog>
  </ValidationObserver>
</template>

<script>
import { ValidationObserver, ValidationProvider } from '~/utils/vee_validate_compat';
import AppDialog from '~/components/AppDialog.vue';
import AppInput from '~/components/AppInput.vue';
import AppCheckbox from '~/components/AppCheckbox.vue';
import AppButton from '~/components/AppButton.vue';
import AppLazySelect from '~/components/AppLazySelect.vue';
import { GlobalTodosResource } from '~/resources';
import { handleFormErrors } from '~/utils/form_utils';

export default {
  name: 'GlobalTodoFormDialog',
  components: {
    ValidationObserver,
    ValidationProvider,
    AppDialog,
    AppInput,
    AppCheckbox,
    AppButton,
    AppLazySelect,
  },
  props: {
    organizationId: {
      type: [String, Number],
      required: true,
    },
    globalTodo: {
      type: Object,
      default: null,
    },
  },
  emits: ['close', 'save-success'],
  data() {
    return {
      form: this.$inertia.form({
        name: this.globalTodo?.name,
        add_new_projects_enabled: this.globalTodo?.add_new_projects_enabled,
        project_ids: this.globalTodo?.project_ids || [],
      }),
      isValidationObserverMounted: false,
    };
  },
  computed: {
    selectedProjectIds: {
      get() {
        return this.form.project_ids || [];
      },
      set(projectIds = []) {
        this.form.project_ids = projectIds;
      },
    },
    isEditMode() {
      return !!this.globalTodo?.id;
    },
    formUrl() {
      return this.isEditMode
        ? `/organizations/${this.organizationId}/global_todos/${this.globalTodo.id}`
        : `/organizations/${this.organizationId}/global_todos`;
    },
  },
  methods: {
    closeDialog() {
      this.$emit('close');
    },
    fetchProjects(payload) {
      return GlobalTodosResource.projects({ organizationId: this.organizationId }, payload, {
        abortReload: true,
      });
    },
    async onFormSubmit() {
      const validated = await this.$refs.dialogObserver.validate();

      if (!validated) {
        return;
      }

      const submissionData = {
        global_todo: this.form.data(),
      };
      const options = {
        preserveState: true,
        preserveScroll: true,
        onSuccess: () => {
          this.$emit('save-success');
          this.closeDialog();
        },
        onError: (errors) => {
          handleFormErrors(this.$refs.dialogObserver, errors, this.$appMessage);
        },
      };
      const formWithData = this.form.transform(() => submissionData)

      return this.isEditMode
        ? formWithData.put(this.formUrl, options)
        : formWithData.post(this.formUrl, options);
    },
  },
};
</script>
